import React from 'react'
import PropTypes from 'prop-types'
import { List, Separator } from '@fs/zion-ui'
import { css } from '@linaria/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { ListItemConclusion } from '@fs/zion-conclusion'
import TempCouple from './TempCouple'
import FamilyLabel from './FamilyLabel'
import CustomPerson from './CustomPerson'

const familyMembersCss = css`
  --person-spacing: 4px;

  &[parents-hidden] {
    padding-top: 8px;
  }
`

export default function Family({
  family,
  type = 'couple',
  event,
  hidePids,
  showGenderText,
  hideParentsLabel,
  focusPersonId,
  childrenLength,
  customPersonLinkOrOverlay,
}) {
  const { parent1, parent2, children = [] } = family
  return (
    <div className={familyMembersCss} parents-hidden={boolAttr(hideParentsLabel)}>
      {!hideParentsLabel && <FamilyLabel.Couple type={type} />}

      <TempCouple
        parent1={parent1}
        parent2={parent2}
        hidePids={hidePids}
        focusPersonId={focusPersonId}
        showGenderText={showGenderText}
        customPersonLinkOrOverlay={customPersonLinkOrOverlay}
      />

      {event ? (
        <List>
          <ListItemConclusion conclusion={event} hideWarnings />
        </List>
      ) : (
        <Separator size="xs" />
      )}

      {children.length > 0 && (
        <>
          <FamilyLabel.Children type={type} focusPersonId={focusPersonId} childrenLength={childrenLength} />

          <List>
            {children.map((child) => {
              const personHasFocus = focusPersonId && focusPersonId === child.id
              const childKey = `child-${child.id}`
              return (
                <CustomPerson
                  key={childKey}
                  person={child}
                  showGenderText={showGenderText}
                  hidePids={hidePids}
                  customPersonLinkOrOverlay={customPersonLinkOrOverlay}
                  highlight={personHasFocus}
                />
              )
            })}
          </List>
        </>
      )}
    </div>
  )
}

Family.propTypes = {
  /** Family to display. */
  family: PropTypes.shape({
    // Parent 1 person object.
    parent1: PropTypes.shape({}),
    // Parent 2 person object.
    parent2: PropTypes.shape({}),
    // Array of person objects.
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,

  /** Determines if this is displaying spouse/children or parents/siblings. */
  type: PropTypes.oneOf(['couple', 'parents']),

  /** Any relationship event. Marriages should be used before any other type of relationship event. */
  event: PropTypes.shape({}),

  /** If true, no pids will be displayed. */
  hidePids: PropTypes.bool,

  /** If true, the "Spouse"/"Parents" label will be hidden. */
  hideParentsLabel: PropTypes.bool,

  /** If this PID is in the persons displayed, they will be higlighted. */
  focusPersonId: PropTypes.string,

  /** If true, will show the all person's gender instead of PID. */
  showGenderText: PropTypes.bool,

  /**
   * If defined and person has customPersonLink, the person's name is linked to the customPersonLink.
   * If defined and person does not have a customPersonLink, this function is called to open the custom overlay.
   */
  customPersonLinkOrOverlay: PropTypes.func,
}
